import { HTTP_STATUS_CODE, IHttpGet } from '../../adapters/http';
import { IRequestDTO, IResponseDTO } from './dto';
import {
  ICalculatedRiskReturnChart,
  IComparisonAssetsStats,
  IComparisonStats,
  IGetAssetColorRepo,
  IGetAssetMemory,
  ISetAssetMemory,
} from '../../interfaces';

export class ComparisonAssetStatsUseCase {
  constructor(
    private readonly url: string,
    private readonly http: IHttpGet<IComparisonStats>,
    private readonly calculateRiskReturnChart: (
      assetsStats: IComparisonStats[] | undefined
    ) => ICalculatedRiskReturnChart[],
    private readonly getAssetColorRepo: IGetAssetColorRepo,
    private readonly assetMemoryRepo: IGetAssetMemory<IComparisonStats> &
      ISetAssetMemory<IComparisonStats>
  ) {}

  public async execute({
    assetsIds,
    start_date = undefined,
    end_date = undefined,
  }: IRequestDTO): Promise<IResponseDTO> {
    const assetsIdsWithNoRepeatValues = [...new Set(assetsIds)];

    const assetsStats = await Promise.all(
      assetsIdsWithNoRepeatValues.map(async (id) => {
        const assetInMemory = this.assetMemoryRepo.get(id);

        if (assetInMemory) {
          return assetInMemory;
        }

        const { statusCode, body } = await this.http.get({
          url: `${this.url}/${id}`,
          params: {
            start_date,
            end_date,
          },
        });

        if (statusCode === HTTP_STATUS_CODE.ok) {
          const newAsset = {
            ...body,
            id,
            stats: this.calculateMonthsPercentage(body.stats),
          };

          this.assetMemoryRepo.set(id, newAsset);

          return newAsset;
        }

        return null;
      })
    );

    const assetsStatsWithoutNullValues = assetsStats.filter(Boolean);

    const assetsStatsWithColor = assetsStatsWithoutNullValues.map((asset) => ({
      ...asset,
      color: this.getAssetColorRepo.getColor(asset.id),
    }));

    const calculatedRiskReturnChart =
      this.calculateRiskReturnChart(assetsStatsWithColor);

    return {
      assetsStats: assetsStatsWithColor,
      calculatedRiskReturnChart,
    };
  }

  private calculateMonthsPercentage(stats: IComparisonAssetsStats) {
    const { positive_months, negative_months } = stats;

    const positive_months_percent =
      (positive_months / (positive_months + negative_months)) * 100;
    const negative_months_percent =
      (negative_months / (positive_months + negative_months)) * 100;

    return {
      ...stats,
      positive_months_percent,
      negative_months_percent,
    };
  }
}
